import Lenis from 'lenis';
import 'lenis/dist/lenis.css';

const media = (query: string) => typeof window !== 'undefined' && window.matchMedia(query).matches;

export const prefersReducedMotion = () => media('(prefers-reduced-motion: reduce)');
/** Mouse or trackpad: the only input where a cursor follower and wheel smoothing make sense. */
export const hasFinePointer = () => media('(hover: hover) and (pointer: fine)');

/**
 * Inertial wheel scrolling for the wall. Touch keeps native momentum. Sheets
 * lock the body with overflow:hidden, so smoothing steps aside while one is
 * open, and anything inside a dialog or a nested scroller keeps native wheel.
 */
export function startSmoothScroll(): () => void {
  if (!hasFinePointer() || prefersReducedMotion()) return () => undefined;
  const lenis = new Lenis({
    lerp: 0.1,
    autoRaf: true,
    allowNestedScroll: true,
    prevent: (node) => Boolean(node.closest('[role="dialog"], [role="menu"], .sheet, [data-lenis-prevent]')),
    virtualScroll: () => document.body.style.overflow !== 'hidden',
  });
  return () => lenis.destroy();
}

let revealObserver: IntersectionObserver | null = null;

/**
 * Scroll reveal: an element rises into place the first time it enters the
 * viewport. Delay follows horizontal position so a row sweeps left to right.
 */
export function observeReveal(el: HTMLElement): () => void {
  if (prefersReducedMotion() || typeof IntersectionObserver === 'undefined') {
    el.dataset.revealed = 'true';
    return () => undefined;
  }
  revealObserver ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const target = entry.target as HTMLElement;
        const x = Math.max(0, entry.boundingClientRect.left) / window.innerWidth;
        target.style.setProperty('--reveal-delay', `${Math.round(x * 220)}ms`);
        target.dataset.revealed = 'true';
        revealObserver?.unobserve(target);
      }
    },
    { rootMargin: '0px 0px -6% 0px' }
  );
  revealObserver.observe(el);
  return () => revealObserver?.unobserve(el);
}
